import { RequestResponse } from '@/app/common/types_and_interfaces'
import { parseRequestError } from '@/app/common/utilities'
import { useMutation } from '@tanstack/react-query'
import axiosInstance from '@/libs/axios_config'
import {
  FamilyApplicationEndpoints,
  FamilyAttestationSchema,
  RelationToStudentSchema,
  StudentSchema,
  YesOrNo,
} from './requests'
import { z } from 'zod'

export const LeaOptionSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
])
export type LeaParticipationOption = z.infer<typeof LeaOptionSchema>
export const EducationPlansSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
])
export type EducationPlansnOption = z.infer<typeof EducationPlansSchema>
export type UpdateStudentRequest = {
  first_name?: string
  last_name?: string
  middle_name?: string | null
  grade?: string
  relationship?: RelationToStudentSchema
  program_id: number
  student_id?: number
  date_of_birth?: string
  SSN?: string
  SSID?: string | null
  lea_participation?: LeaParticipationOption
  competing_scholarship_ids?: number[]
  applicant_doesnt_have_birth_cert?: boolean
  education_plan?: EducationPlansnOption
  require_special_education?: YesOrNo
  force?: boolean
  document_name?: string
  document_description?: string
  agree_external_document?: number
}

const StudentDetailsResponseSchema = StudentSchema.and(
  z.object({
    attestations: z.array(FamilyAttestationSchema).min(0).optional(),
  })
)
type StudentDetailsResponse = z.infer<typeof StudentDetailsResponseSchema>
export async function POSTStudentDetails(
  payload: UpdateStudentRequest
): Promise<RequestResponse<StudentDetailsResponse>> {
  try {
    const response = await axiosInstance.post(
      FamilyApplicationEndpoints.update_student,
      payload
    )

    return {
      data: StudentDetailsResponseSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    console.error(error)
    return { data: null, error: parseRequestError(error) }
  }
}

const UpdateScholarshipRequestSchema = z.object({
  student_id: z.number(),
  status: z.number(),
})

const UpdateScholarshipResponseSchema = z.object({
  message: z.string(),
})

type UpdateScholarshipRequest = z.infer<typeof UpdateScholarshipRequestSchema>
type UpdateScholarshipResponse = z.infer<typeof UpdateScholarshipResponseSchema>

// Define the custom hook
export const useUpdateScholarshipApplicationStatus = () => {
  return useMutation<
    UpdateScholarshipResponse,
    Error,
    UpdateScholarshipRequest
  >({
    mutationFn: async (payload: UpdateScholarshipRequest) => {
      try {
        const response = await axiosInstance.put(
          FamilyApplicationEndpoints.update_scholarship_applications,
          payload
        )
        return UpdateScholarshipResponseSchema.parse(response.data)
      } catch (error) {
        throw parseRequestError(error)
      }
    },
  })
}
